/**
 * What a client downloads from a pack, and how much of it is waste.
 *
 * A server owner drags a `resources/` folder in and asks one question: where did
 * the 3 GB go? Every part of that question is about *bytes on disk*, which is
 * why this module reads no file contents, parses nothing, and judges no
 * resource. It counts, it groups, and it says which counts are out of line.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A TOTAL IS THE HARD PART
 *
 * The tool this replaces is called a load checker and measures no load: nothing
 * it reports correlates with tick time, thread count or frame rate. A 900 MB map
 * pack of collision meshes costs a server essentially nothing; a 40 KB Lua file
 * with a busy loop kills it. So the name here is the honest one — a budget is a
 * thing you can be over — and the number the name promises has to be real.
 *
 * Theirs is not. It admits eighteen extensions plus anything under `stream/`,
 * then labels the result "Total size" with no qualifier. A pure script resource
 * reports zero bytes. In one real scan the unnamed remainder was 203 MB of
 * 391 MB, and the interface presents four tiles that invite the reading
 * `Total = Textures + Models`. So the first rule of this module is the one that
 * costs the most to keep:
 *
 *   EVERY FILE IN THE DROP LANDS IN EXACTLY ONE BUCKET, AND THE BUCKETS SUM TO
 *   `totalBytes`. A bucket called `other` that is honestly large beats a total
 *   that lies.
 *
 * Two consequences of that rule are not obvious and are the two places a naive
 * implementation silently goes wrong:
 *
 *  1. The input is `drop.allPaths`, never `drop.resources[].files`. `allPaths`
 *     is intake's unfiltered inventory — manifests, scripts, readmes, files
 *     belonging to no resource at all. `resources[].files` is the *analysable*
 *     subset, and it is missing every entry whose bytes intake could not get.
 *     Sizing a folder from the analysable subset is how a tool reports a clean
 *     390 MB for a 590 MB download.
 *
 *  2. An archive that intake opened appears in `allPaths` twice over: once as
 *     the `.zip` file the user has on disk, and again as every entry inside it
 *     at its uncompressed size. Adding both produces a number that is neither
 *     the download nor the disk. Opened archives are therefore counted through
 *     their entries and excluded from the total themselves, and the report says
 *     so in `skipped`. An archive that could *not* be opened — a `.rpf`, a zip
 *     nested too deep — keeps its own bytes and is named, because those bytes
 *     are real and their contents are unknown.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * GROUPING, AND THE BUG THIS EXISTS TO NOT HAVE
 *
 * The resource is the primary unit. Their grouping function looks for
 * `fxmanifest.lua` in a list that by construction has already had manifests
 * filtered out of it, so the branch is unreachable code and the fallback keys
 * off whichever path the archive happened to enumerate first. Reorder a zip's
 * central directory and a different resource gets its own row.
 *
 * Here the roots come from `intake.ts`, which found them structurally over the
 * unfiltered list — a directory holding a manifest, or holding a `stream/`
 * folder or a `.meta` when no manifest exists anywhere above it. This module
 * attributes each path to the *deepest* root that contains it and never invents
 * one. Anything under no root at all is one clearly-named row rather than being
 * folded into the first resource or dropped: a 200 MB `backups/` folder beside
 * `resources/` is exactly the kind of thing a user is looking for.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE RULES ARE AGGREGATE
 *
 * Every one of the five rules in the tool this replaces is per-file, so fifty
 * 15 MB texture dictionaries is 750 MB and zero flags. That is the most common
 * real bloat pattern on a busy server and the tool is structurally blind to it.
 * Four of the seven codes here are therefore totals — per resource, per
 * extension, per share — and the per-file rule keeps a near-budget tier at 80%
 * of its ceiling so a boundary case is visible before it crosses.
 *
 * The near tier is deliberately *not* applied to the share rules. A share has no
 * ceiling a reader can act on: 64% textures is not "approaching" anything, it is
 * a vehicle pack. A near tier on a ratio is noise wearing the costume of rigour.
 *
 * Calibration is the whole product. A well-made add-on vehicle must pass clean,
 * because a checker that cries wolf gets ignored and then the one real finding
 * goes unread. Two fixes over their thresholds, both from the same principle —
 * advice you cannot act on is worse than silence:
 *
 *  - `.ypt` is a particle dictionary, not a texture dictionary. Telling somebody
 *    to DXT-compress one is unactionable, so it has its own ceiling and its own
 *    sentence.
 *  - Their "unusually large asset" rule fires on collision meshes, animation
 *    dictionaries and map data with one generic sentence and no remedy. Each of
 *    those is a different mistake with a different fix, so each gets its own row
 *    in `ASSET_CEILINGS` and its own remedy.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * EXACT BYTES, EVERYWHERE A THRESHOLD IS APPLIED
 *
 * Theirs rounds both 16,777,215 and 16,777,216 to "16 MB", so two adjacent rows
 * read identically while only one carries a warning dot — the number that
 * decides the verdict is the one number the interface refuses to show. Every
 * row here carries `bytes` as an integer, every finding prints
 * `formatBudgetBytes`, and that function always ends in the exact count.
 *
 * `formatBytes` in `resmon.ts` already prints the tier half of that string, and
 * this module still does not import it — for the reason `csv.ts` sets out at
 * length: `resmon.ts` is a 93 KB log parser, and reaching into it for eight
 * lines drags the whole thing into every bundle that wanted a folder scan. The
 * two formatters also answer different questions: `formatBytes` returns an em
 * dash for a figure a dump never printed, and there is no such thing here — a
 * file always has a size.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SECOND PASS IS THE CALLER'S, AND WHAT IT PROVES
 *
 * Duplicate detection and the identical high-detail check both need content, and
 * reading two 10 MB models to compare them is a decision that belongs to
 * whoever is paying for it — the same rule `GtaResourceInput.identicalPairs`
 * already follows: the analyzer asks for the answer, not for the bytes.
 *
 * So the caller runs `duplicateCandidatePaths` first, which returns only the
 * files whose size is shared with another file and therefore *could* be
 * duplicates, reads `SIGNATURE_PREFIX_BYTES` from each, and hands back a map of
 * `budgetSignature` values. Two files match when their size and their prefix
 * hash both match. That is strong evidence and it is not proof, so every
 * sentence this module writes about it says which comparison was made, and
 * `signatureKind: "full-content"` is there for a caller who did the whole read
 * and can say so.
 *
 * With no signatures at all, both checks are skipped — in words, in `skipped`.
 * A silent "no duplicates found" over files nobody compared is the exact failure
 * this layer is built against.
 *
 * Every sentence written about that pass counts against the *same* set
 * `duplicateCandidatePaths` returns, and that is load bearing rather than
 * tidiness. Counting "files not compared" over every file above the floor
 * instead would fire on essentially every scan, because a file whose length
 * occurs once in the drop is never signed and never needed to be — and a caveat
 * list that cries wolf on every scan is read exactly as carefully as the
 * competitor's unqualified "Total size". `comparableRows` is that set.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LIMITS, STATED PLAINLY
 *
 *  - `totalBytes` is bytes on disk. It is not VRAM. A `.ytd` begins `RSC7` and
 *    its header carries the virtual and physical page counts FiveM's own asset
 *    budget is expressed in, and `src/lib/yftYtd.ts` in this repo can already
 *    read them — turning on-disk size into a real VRAM figure is a second phase
 *    with a clean seam: it would add a field beside `bytes` on `BudgetFileRow`
 *    and a bucket tally beside it, and change nothing else here. Nothing in this
 *    module imports that reader, and no number it produces should be presented
 *    as memory pressure until it does.
 *  - It is not "what the client downloads" to the byte either, and says so
 *    rather than implying it: a `server_script` is never sent to a client, and a
 *    file the manifest never names is downloaded by nobody. Deciding which
 *    subset ships needs the manifest, and a pack whose manifest is wrong is the
 *    pack most likely to be scanned. The honest claim is the one made above —
 *    every byte in the drop, in exactly one bucket — and it is checkable.
 *  - A bucket is a spending category, not a load route. `.awc` is filed under
 *    `audio` because that is where its megabytes come from, while
 *    `isStreamExtension` remains the only authority on how it reaches the game.
 *    Nothing here is a statement about whether a file needs declaring.
 *  - Nothing throws and nothing is read from the network. A malformed row is
 *    skipped and counted, never a reason to fail a scan of nine hundred others.
 */

import { csvCell } from "./csv";
import { MANIFEST_DECLARED_EXTENSIONS, STREAM_EXTENSIONS } from "./data/dataFileKinds.v1";
import { dedupeFindings, sortFindings } from "./findings";
import type { ResourceDrop } from "./intake";
import { disambiguateLabels, EM_DASH_LABEL_STYLE } from "./labels";
import { baseName, extensionOf, normalizePath, pathKey } from "./normalize";
import { collectStreamAssets, highLodGroups } from "./streamAssets";
import type { GtaFile, GtaFinding, GtaSeverity, StreamAsset } from "./types";

// ─── Caps ────────────────────────────────────────────────────────────────────

/**
 * The most paths walked. Intake's own ceiling is 50,000 entries per drop, so
 * this is four times a full drop and exists for the caller who assembled a
 * `ResourceDrop` by hand. Whatever it cuts is said out loud in `skipped`.
 */
const MAX_PATHS = 200_000;

/** Rows in `largest`, which the brief's layout renders as one table. */
const DEFAULT_LARGEST = 15;

/**
 * The most findings any one rule contributes.
 *
 * A 2,000-file pack of oversized textures produces 2,000 identical sentences,
 * and past a certain length a findings list is not read at all. The overflow is
 * not hidden: the count goes into `skipped`, and every file is still in
 * `byResource[].assets` with its own tier.
 */
const MAX_FINDINGS_PER_RULE = 40;

/** Copies named inside one duplicate finding, which is one paragraph in a card. */
const MAX_COPIES_NAMED = 8;

// ─── Buckets ─────────────────────────────────────────────────────────────────

export type BudgetBucket = "stream" | "script" | "audio" | "data" | "media" | "other";

/** Presentation order: biggest-spending categories first, `other` last. */
export const BUDGET_BUCKETS: readonly BudgetBucket[] = [
  "stream",
  "audio",
  "script",
  "data",
  "media",
  "other",
];

export const BUDGET_BUCKET_LABELS: Record<BudgetBucket, string> = {
  stream: "Stream assets",
  script: "Scripts",
  audio: "Audio",
  data: "Data and config",
  media: "Images, video and UI",
  other: "Everything else",
};

function seed(extensions: readonly string[], bucket: BudgetBucket): Record<string, BudgetBucket> {
  const out: Record<string, BudgetBucket> = {};
  for (const extension of extensions) out[extension] = bucket;
  return out;
}

/**
 * Extension → bucket.
 *
 * Seeded from the two shared tables rather than retyped, because the failure
 * this whole module is written against is an unnamed remainder: a fourteenth
 * entry added to `STREAM_EXTENSIONS` would otherwise land silently in `other`
 * and a map pack's largest column would quietly stop being stream assets.
 *
 * The explicit rows below the seeds win, and the divergence is deliberate. Those
 * tables answer "how does this file reach the game"; a bucket answers "where did
 * my megabytes go". `.awc` is a streamed extension and is filed under `audio`,
 * because a user looking at 400 MB of audio wants it counted as audio and does
 * not care that the streamer is what loads it. Nothing here is a statement about
 * whether a file needs declaring — `isStreamExtension` stays the only authority
 * on that.
 *
 * An extension in neither table and absent below falls to `other`, which is the
 * point of `other`.
 */
const BUCKET_BY_EXTENSION: Record<string, BudgetBucket> = {
  // Everything FiveM streams by bare basename: geometry, textures, map data.
  ...seed(STREAM_EXTENSIONS, "stream"),
  // Everything that reaches the game only because a manifest line names it:
  // `.rel`, `.dat`, `.gxt2`. Two of the three are refined immediately below.
  ...seed(MANIFEST_DECLARED_EXTENSIONS, "data"),

  // Audio: the wave banks, the compiled metadata beside them, loose sources.
  awc: "audio",
  rel: "audio",
  ogg: "audio",
  oga: "audio",
  wav: "audio",
  mp3: "audio",
  flac: "audio",
  opus: "audio",
  m4a: "audio",
  aac: "audio",

  // Scripts, in every form FiveM runs one.
  lua: "script",
  luac: "script",
  js: "script",
  mjs: "script",
  cjs: "script",
  ts: "script",
  tsx: "script",
  jsx: "script",
  dll: "script",
  wasm: "script",

  // Data and config.
  meta: "data",
  xml: "data",
  json: "data",
  gxt2: "data",
  cfg: "data",
  ini: "data",
  sql: "data",
  yml: "data",
  yaml: "data",
  toml: "data",
  csv: "data",

  // What a NUI page ships.
  html: "media",
  htm: "media",
  css: "media",
  scss: "media",
  less: "media",
  png: "media",
  jpg: "media",
  jpeg: "media",
  gif: "media",
  webp: "media",
  svg: "media",
  ico: "media",
  bmp: "media",
  tga: "media",
  dds: "media",
  mp4: "media",
  webm: "media",
  ogv: "media",
  mov: "media",
  ttf: "media",
  otf: "media",
  woff: "media",
  woff2: "media",
  eot: "media",
};

/**
 * `.dat` is two different files wearing one extension.
 *
 * `audioconfig/dlcambo_game.dat151.rel` has a `.dat151.rel` sibling set that is
 * audio metadata; `data/relationships.dat` is a world data file. The path is
 * what separates them, so the bucket is decided by the path and not by the
 * extension alone — the alternative is filing every `.dat` as audio and
 * inventing a 40 MB audio bucket for a pack with no audio in it.
 */
const AUDIO_PATH_HINT = /(^|\/)(audio|audioconfig|audiodirectory|sfx)(\/|$)/;

export function bucketOf(path: string): BudgetBucket {
  const extension = extensionOf(path);
  if (extension === "dat") return AUDIO_PATH_HINT.test(pathKey(path)) ? "audio" : "data";
  return BUCKET_BY_EXTENSION[extension] ?? "other";
}

// ─── Per-file ceilings ───────────────────────────────────────────────────────

const KiB = 1024;
const MiB = 1024 * 1024;

/**
 * What kind of asset a file is, for the purpose of how big it is allowed to be.
 *
 * Finer-grained than a bucket on purpose. Their single "unusually large asset"
 * rule fires on a collision mesh, an animation dictionary and a map section with
 * one generic sentence, which is three different mistakes given one non-remedy.
 */
export type BudgetAssetKind =
  | "texture"
  | "model"
  | "particle"
  | "collision"
  | "navmesh"
  | "animation"
  | "map"
  | "audio"
  | "other";

export type BudgetCeiling = {
  /** How a finding names the file: "texture dictionary", "collision mesh". */
  label: string;
  /** At or above this many bytes, a warning. */
  warn: number;
  /** At or above this many bytes, a warning with the stronger sentence. */
  high: number;
  /** What to actually do about it. One sentence, specific to this kind. */
  advice: string;
};

/**
 * The ceilings, and the remedy each one implies.
 *
 * 16 MB to warn on a texture dictionary and 32 for high, 16 and 48 for models,
 * 64 for anything else, are the competitor's numbers and they are defensible:
 * `20ambocr`, a real published vehicle, passes every one of them. Megabytes are
 * MiB throughout, which is what a file manager shows and what makes 16 MB
 * exactly 16,777,216 bytes — the number the boundary case turns on.
 */
export const ASSET_CEILINGS: Record<BudgetAssetKind, BudgetCeiling> = {
  texture: {
    label: "texture dictionary",
    warn: 16 * MiB,
    high: 32 * MiB,
    advice:
      "Re-save it with DXT/BC compression and mipmaps, or step the largest sheets down from " +
      "4096 to 2048. An uncompressed 4K sheet is roughly four times the size of a compressed one " +
      "and looks the same at the distance a player sees it.",
  },
  model: {
    label: "model",
    warn: 16 * MiB,
    high: 48 * MiB,
    advice:
      "A drawable this size is usually one uncollapsed high-detail mesh with no LODs. Generate " +
      "L1–L3 LODs, or split the parts into separate drawables so the streamer can load them " +
      "independently.",
  },
  particle: {
    label: "particle dictionary",
    warn: 8 * MiB,
    high: 16 * MiB,
    advice:
      "A .ypt carries its own sprite sheets, so its size is almost always one oversized sheet " +
      "inside it. Shrink the sheets. Do not try to DXT-compress the dictionary itself — that is " +
      "texture-dictionary advice and it does not apply to a particle file.",
  },
  collision: {
    label: "collision mesh",
    warn: 32 * MiB,
    high: 64 * MiB,
    advice:
      "A .ybn this size is a collision mesh built from the render mesh. Replace it with a " +
      "simplified hull or primitive boxes: players never see it, and the physics engine pays " +
      "for every triangle in it.",
  },
  navmesh: {
    label: "navigation mesh",
    warn: 32 * MiB,
    high: 64 * MiB,
    advice:
      "A .ynv this size covers far more ground than one resource usually changes. Regenerate it " +
      "for the area you actually edited rather than shipping the region it was cut from.",
  },
  animation: {
    label: "animation dictionary",
    warn: 16 * MiB,
    high: 32 * MiB,
    advice:
      "An animation dictionary this size is usually every clip the author ever exported. Ship " +
      "only the clips this resource plays; the rest are downloaded by every client and never " +
      "referenced.",
  },
  map: {
    label: "map data",
    warn: 16 * MiB,
    high: 48 * MiB,
    advice:
      "Map data this size is one enormous section, which the streamer can only load or unload " +
      "as a whole. Split it into several .ymap files covering smaller areas.",
  },
  audio: {
    label: "audio bank",
    warn: 32 * MiB,
    high: 64 * MiB,
    advice:
      "An .awc this size is usually 48 kHz stereo where the game plays mono. Re-encode at the " +
      "rate and channel count the bank actually needs.",
  },
  other: {
    label: "file",
    warn: 64 * MiB,
    high: 128 * MiB,
    advice:
      "Nothing the game streams is this shape, so check whether it needs to ship at all. Source " +
      "art, backups and archives left inside a resource are downloaded by every client on " +
      "connect and read by nothing.",
  },
};

const ASSET_KIND_BY_EXTENSION: Record<string, BudgetAssetKind> = {
  ytd: "texture",
  yft: "model",
  ydr: "model",
  ydd: "model",
  ypt: "particle",
  ybn: "collision",
  ynv: "navmesh",
  ycd: "animation",
  ymap: "map",
  ytyp: "map",
  ymt: "map",
  ynd: "map",
  awc: "audio",
};

export function assetKindOf(path: string): BudgetAssetKind {
  return ASSET_KIND_BY_EXTENSION[extensionOf(path)] ?? "other";
}

// ─── Aggregate thresholds ────────────────────────────────────────────────────

export type BudgetThresholds = {
  /** One resource's total, at or above which `budget.resource.total` fires. */
  resourceBytesWarn: number;
  resourceBytesHigh: number;
  /** One resource's file count, for `budget.resource.count`. */
  resourceFilesWarn: number;
  resourceFilesHigh: number;
  /** Share of the whole drop one extension has to reach to be "dominating". */
  extensionShareWarn: number;
  extensionShareHigh: number;
  /**
   * And the absolute floor under which that share means nothing.
   *
   * A 40 MB vehicle pack that is 60% `.yft` is a vehicle pack. Without a floor
   * this rule fires on every correct small pack there is, which is the
   * cry-wolf failure the whole calibration exists to avoid.
   */
  extensionBytesFloor: number;
  /** Texture share of one resource, for `budget.texture.share`. */
  textureShareWarn: number;
  textureShareHigh: number;
  /** And its floor, on the same argument. */
  textureBytesFloor: number;
  /**
   * Smallest file worth comparing for duplication.
   *
   * Every pack ships an identical 300-byte `fxmanifest.lua` and a dozen
   * identical small metas. Reporting those as waste is noise that hides the
   * 12 MB dictionary shipped six times, which is the finding that matters.
   */
  duplicateMinBytes: number;
  /** Wasted bytes at or above which a duplicate is a warning rather than info. */
  duplicateWasteWarn: number;
  /** The near-budget tier, as a fraction of the warn ceiling. */
  nearFraction: number;
};

export const BUDGET_THRESHOLDS: BudgetThresholds = {
  resourceBytesWarn: 256 * MiB,
  resourceBytesHigh: 512 * MiB,
  resourceFilesWarn: 1_500,
  resourceFilesHigh: 5_000,
  extensionShareWarn: 0.7,
  extensionShareHigh: 0.85,
  extensionBytesFloor: 128 * MiB,
  textureShareWarn: 0.8,
  textureShareHigh: 0.92,
  textureBytesFloor: 64 * MiB,
  duplicateMinBytes: 64 * KiB,
  duplicateWasteWarn: 4 * MiB,
  nearFraction: 0.8,
};

/** Where a measurement sits against its ceiling. `null` is comfortably under. */
export type BudgetTier = "near" | "warn" | "high";

/**
 * At or above, not over.
 *
 * `>=` is what makes 16,777,216 bytes cross a 16 MiB ceiling and 16,777,215 not,
 * which is the boundary the interface has to be able to show and theirs cannot.
 */
function tierOf(value: number, warn: number, high: number, nearFraction: number): BudgetTier | null {
  if (!Number.isFinite(value)) return null;
  if (value >= high) return "high";
  if (value >= warn) return "warn";
  if (nearFraction > 0 && value >= warn * nearFraction) return "near";
  return null;
}

const TIER_SEVERITY: Record<BudgetTier, GtaSeverity> = {
  near: "info",
  warn: "warning",
  high: "warning",
};

// ─── Formatting ──────────────────────────────────────────────────────────────

const BYTE_TIERS: ReadonlyArray<readonly [string, number]> = [
  ["GiB", 1024 ** 3],
  ["MiB", 1024 ** 2],
  ["KiB", 1024],
];

/**
 * A size a reader can scan and a number a reader can act on, in one string.
 *
 * Always both. The rounded figure is what makes a table readable; the exact
 * count is what makes two adjacent rows distinguishable when one of them
 * carries a warning and the other does not.
 */
export function formatBudgetBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return "0 bytes";
  const exact = `${Math.round(bytes).toLocaleString("en-US")} bytes`;
  const abs = Math.abs(bytes);
  for (const [unit, size] of BYTE_TIERS) {
    if (abs >= size) return `${(bytes / size).toFixed(2)} ${unit} (${exact})`;
  }
  return exact;
}

/** A share as a whole per cent, which is as precise as a share ever needs to be. */
function formatShare(share: number): string {
  return `${Math.round(share * 100)}%`;
}

/**
 * Ordering a saved report can be diffed against.
 *
 * `localeCompare` depends on the ICU data of the machine it runs on, so the same
 * drop scanned in two places produces two orderings and a saved report starts
 * showing a diff nobody made. `streamAssets.ts` makes the same argument.
 */
function compareText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

// ─── The report shape ────────────────────────────────────────────────────────

export type BudgetTally = { bytes: number; files: number };

export type BudgetFileRow = {
  /** Drop-relative, as `allPaths` spells it. Unique within a report. */
  path: string;
  name: string;
  extension: string;
  /** The resource's disambiguated label, so a row and a finding agree. */
  resource: string;
  bucket: BudgetBucket;
  kind: BudgetAssetKind;
  /** Exact. Every threshold in this module is applied to this number. */
  bytes: number;
  /** Against this kind's own ceiling. */
  tier: BudgetTier | null;
};

export type BudgetResourceRow = {
  /** What FiveM would call it. Allowed to repeat — that repetition is a finding. */
  name: string;
  /** What to print: widened only when two rows would otherwise read alike. */
  label: string;
  /** The drop-relative root. Unique within a drop, empty for the loose row. */
  identity: string;
  origin?: string;
  /**
   * `unassigned` is the row for files under no resource root at all — a
   * `backups/` folder beside `resources/`, a stray archive, a readme at the top
   * level. It is counted and named rather than folded into a neighbour, and the
   * per-resource rules skip it because it is not a resource.
   */
  kind: "resource" | "unassigned";
  bytes: number;
  files: number;
  buckets: Record<BudgetBucket, BudgetTally>;
  /** Every file in this resource, largest first. The expandable half of a row. */
  assets: BudgetFileRow[];
};

export type BudgetDuplicateGroup = {
  /** `size:hash` as `budgetSignature` built it. */
  signature: string;
  /** The size of one copy. Every copy in the group has it. */
  bytes: number;
  copies: { path: string; resource: string }[];
  /** What deleting all but one would save. */
  wastedBytes: number;
};

export type BudgetLodPair = {
  /** The base asset, drop-relative. */
  basePath: string;
  /** Its `_hi` / `+hi` companion. */
  companionPath: string;
  resource: string;
  bytes: number;
};

export type BudgetReport = {
  /**
   * Every byte in the drop, opened archives counted once through their entries.
   * On disk, not in VRAM — see the module header.
   */
  totalBytes: number;
  totalFiles: number;
  byBucket: Record<BudgetBucket, BudgetTally>;
  /** Largest first. */
  byResource: BudgetResourceRow[];
  /** Largest first. `""` is the row for files with no extension. */
  byExtension: { extension: string; bytes: number; files: number }[];
  largest: BudgetFileRow[];
  duplicates: BudgetDuplicateGroup[];
  /** High-detail companions found byte-identical to their base. */
  identicalLodPairs: BudgetLodPair[];
  findings: GtaFinding[];
  /** The ceilings this report was measured against, so a UI can print them. */
  thresholds: BudgetThresholds;
  /**
   * Everything this report did not do, in words a user can act on: what intake
   * could not read, what was not compared, what was counted but not listed.
   * Empty only when the answer really is "all of it".
   */
  skipped: string[];
};

// ─── Signatures: the caller's pass ───────────────────────────────────────────

/**
 * How much of a file a signature reads. Four kilobytes, as the brief specifies.
 *
 * Enough to separate two different 12 MB dictionaries — an RSC7 header carries
 * its page counts and its first table in far less than this — and small enough
 * that signing four hundred candidates is one short read each rather than a
 * second full scan of the pack.
 */
export const SIGNATURE_PREFIX_BYTES = 4096;

/**
 * The signature two files are compared on: their size, and a hash of their
 * first `SIGNATURE_PREFIX_BYTES`.
 *
 * FNV-1a rather than SHA-256 because `crypto.subtle.digest` is async and would
 * make every caller's second pass a promise chain for no gain: this is a
 * dedupe key, not a security claim, and the size is half of it. Defined here
 * rather than left to the caller so that two surfaces signing the same pack
 * cannot disagree about what a match means.
 */
export function budgetSignature(size: number, prefix: Uint8Array): string {
  let hash = 0x811c9dc5;
  const length = Math.min(prefix.length, SIGNATURE_PREFIX_BYTES);
  for (let index = 0; index < length; index += 1) {
    hash ^= prefix[index];
    // The FNV prime, by shifts, because `hash * 16777619` leaves float range.
    hash = (hash + ((hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24))) >>> 0;
  }
  return `${size}:${length}:${hash.toString(16)}`;
}

/**
 * The caller's map, re-keyed through `pathKey`.
 *
 * A caller that signed `stream/Police.ytd` and a drop that lists
 * `police/stream/police.ytd` are talking about paths the same way every other
 * comparison in this layer does — case-folded and `/`-separated — so the lookup
 * goes through the same normalizer rather than through raw text.
 */
function signedByKey(signatures: ReadonlyMap<string, string> | undefined): Map<string, string> {
  const signed = new Map<string, string>();
  if (!signatures) return signed;
  for (const [path, signature] of signatures.entries()) {
    if (typeof path === "string" && typeof signature === "string" && signature !== "") {
      signed.set(pathKey(path), signature);
    }
  }
  return signed;
}

/** What a caller's signatures prove, which decides how a finding is worded. */
export type BudgetSignatureKind = "size-and-prefix" | "full-content";

export type BudgetOptions = {
  /**
   * Signatures keyed by drop-relative path, as `budgetSignature` builds them.
   *
   * Absent means the second pass was not run, which is reported as skipped —
   * never as "no duplicates".
   */
  signatures?: ReadonlyMap<string, string>;
  /** Defaults to `"size-and-prefix"`, which is what `budgetSignature` proves. */
  signatureKind?: BudgetSignatureKind;
  thresholds?: Partial<BudgetThresholds>;
  ceilings?: Partial<Record<BudgetAssetKind, BudgetCeiling>>;
  /** Rows in `largest`. Defaults to fifteen. */
  largestCount?: number;
};

// ─── Inventory ───────────────────────────────────────────────────────────────

type SizedPath = { path: string; key: string; bytes: number };

type Inventory = {
  files: SizedPath[];
  /** Archives intake opened, whose entries are counted instead of themselves. */
  containers: SizedPath[];
  skipped: string[];
};

/** Extensions that hold other files, and so may be counted through them. */
const CONTAINER_EXTENSIONS: ReadonlySet<string> = new Set([
  "zip",
  "rpf",
  "7z",
  "rar",
  "tar",
  "gz",
]);

/**
 * Every countable file in the drop, with opened archives removed.
 *
 * An archive intake *opened* is in `allPaths` next to every one of its entries,
 * so counting both is counting the same bytes twice in two different units. An
 * archive it could not open — a `.rpf`, a zip nested past intake's one level —
 * has no entries in the list, keeps its own bytes, and is already named in
 * `drop.truncated`, which this module folds into `skipped`.
 *
 * "Opened" is asked structurally rather than by trusting an extension: an
 * archive is opened exactly when some other path in the drop is inside it.
 */
function inventory(drop: ResourceDrop): Inventory {
  const skipped: string[] = [];
  const raw = Array.isArray(drop?.allPaths) ? drop.allPaths : [];

  const paths = raw.slice(0, MAX_PATHS);
  if (raw.length > paths.length) {
    skipped.push(
      `This drop lists ${raw.length.toLocaleString("en-US")} files and only the first ` +
        `${MAX_PATHS.toLocaleString("en-US")} were counted, so the totals below are a floor ` +
        "rather than the whole folder.",
    );
  }

  // Every directory any path sits under, so "is anything inside this file" is a
  // set lookup rather than a scan of the whole list per archive.
  const directories = new Set<string>();
  const sized: SizedPath[] = [];
  for (const entry of paths) {
    const path = typeof entry?.path === "string" ? normalizePath(entry.path) : "";
    if (path === "") continue;
    const bytes = Number.isFinite(entry?.size) && entry.size > 0 ? Math.floor(entry.size) : 0;
    const key = pathKey(path);
    sized.push({ path, key, bytes });
    for (let cut = key.lastIndexOf("/"); cut > 0; cut = key.lastIndexOf("/", cut - 1)) {
      directories.add(key.slice(0, cut));
    }
  }

  const files: SizedPath[] = [];
  const containers: SizedPath[] = [];
  for (const entry of sized) {
    if (CONTAINER_EXTENSIONS.has(extensionOf(entry.path)) && directories.has(entry.key)) {
      containers.push(entry);
      continue;
    }
    files.push(entry);
  }

  if (containers.length > 0) {
    const bytes = containers.reduce((total, entry) => total + entry.bytes, 0);
    const named = containers.length === 1 ? containers[0].path : `${containers.length} archives`;
    skipped.push(
      `${named} ${containers.length === 1 ? "was" : "were"} opened, so the files inside ` +
        `${containers.length === 1 ? "it" : "them"} are counted at their uncompressed sizes and ` +
        `the archive ${containers.length === 1 ? "itself is" : "files themselves are"} not ` +
        `counted a second time (${formatBudgetBytes(bytes)} compressed).`,
    );
  }

  return { files, containers, skipped };
}

/**
 * The files worth reading for the second pass, and only those.
 *
 * Two files cannot be duplicates unless they are the same length, so a size that
 * occurs once in the whole drop needs no read at all. On a real server folder
 * that cuts the candidate set by an order of magnitude, which is what makes
 * duplicate detection one short pass rather than a second full scan — the
 * difference between a feature and a feature nobody waits for.
 *
 * Byte-identical high-detail pairs fall out of the same set for free: a `_hi`
 * companion identical to its base is the same length as its base by definition.
 */
export function duplicateCandidatePaths(
  drop: ResourceDrop,
  options?: Pick<BudgetOptions, "thresholds">,
): string[] {
  const thresholds = { ...BUDGET_THRESHOLDS, ...(options?.thresholds ?? {}) };
  const { files } = inventory(drop);

  const counts = new Map<number, number>();
  for (const file of files) {
    if (file.bytes < thresholds.duplicateMinBytes) continue;
    counts.set(file.bytes, (counts.get(file.bytes) ?? 0) + 1);
  }

  return files
    .filter((file) => (counts.get(file.bytes) ?? 0) > 1)
    .map((file) => file.path)
    .sort(compareText);
}

// ─── Grouping ────────────────────────────────────────────────────────────────

type ResourceGroup = {
  name: string;
  identity: string;
  origin?: string;
  kind: "resource" | "unassigned";
  files: SizedPath[];
};

/** The deepest declared root that contains this path, or `null` for none. */
function rootIndexFor(key: string, rootsByKey: ReadonlyMap<string, number>): number | null {
  for (let cut = key.lastIndexOf("/"); cut > 0; cut = key.lastIndexOf("/", cut - 1)) {
    const hit = rootsByKey.get(key.slice(0, cut));
    if (hit !== undefined) return hit;
  }
  // A resource rooted at the drop root itself — a single pack dropped as a zip
  // of its own contents, which is the ordinary shape of a vendor download.
  const top = rootsByKey.get("");
  return top ?? null;
}

/**
 * One row per resource intake found, plus at most one row for everything else.
 *
 * The roots are intake's, found structurally over the unfiltered list. Nothing
 * here invents a root from the first path's first segment, which is the defect
 * this grouping exists to not have.
 */
function groupByResource(drop: ResourceDrop, files: readonly SizedPath[]): ResourceGroup[] {
  const declared = Array.isArray(drop?.resources) ? drop.resources : [];
  const origins = drop?.origins instanceof Map ? drop.origins : new Map<string, string>();

  const groups: ResourceGroup[] = [];
  const rootsByKey = new Map<string, number>();
  for (const resource of declared) {
    if (!resource || typeof resource.root !== "string") continue;
    const root = normalizePath(resource.root);
    const key = pathKey(root);
    if (rootsByKey.has(key)) continue;
    rootsByKey.set(key, groups.length);
    groups.push({
      name: typeof resource.name === "string" && resource.name !== "" ? resource.name : root,
      identity: root,
      origin: origins.get(resource.root) ?? origins.get(root),
      kind: "resource",
      files: [],
    });
  }

  const loose: SizedPath[] = [];
  for (const file of files) {
    const index = rootIndexFor(file.key, rootsByKey);
    if (index === null) loose.push(file);
    else groups[index].files.push(file);
  }

  if (loose.length > 0) {
    groups.push({
      name: "Files outside any resource",
      identity: "",
      kind: "unassigned",
      files: loose,
    });
  }

  return groups;
}

// ─── The analysis ────────────────────────────────────────────────────────────

function emptyTallies(): Record<BudgetBucket, BudgetTally> {
  return {
    stream: { bytes: 0, files: 0 },
    script: { bytes: 0, files: 0 },
    audio: { bytes: 0, files: 0 },
    data: { bytes: 0, files: 0 },
    media: { bytes: 0, files: 0 },
    other: { bytes: 0, files: 0 },
  };
}

function add(tally: Record<BudgetBucket, BudgetTally>, bucket: BudgetBucket, bytes: number): void {
  tally[bucket].bytes += bytes;
  tally[bucket].files += 1;
}

function byBytesDesc<T extends { bytes: number }>(a: T, b: T): number {
  return b.bytes - a.bytes;
}

/**
 * What a drop costs, where it goes, and which of it is waste.
 *
 * Pure, synchronous and read-only over `drop`. Never throws: a malformed entry
 * is skipped, because the useful response to one bad row is the other nine
 * hundred.
 */
export function analyzeBudget(drop: ResourceDrop, options?: BudgetOptions): BudgetReport {
  const thresholds: BudgetThresholds = { ...BUDGET_THRESHOLDS, ...(options?.thresholds ?? {}) };
  const ceilings: Record<BudgetAssetKind, BudgetCeiling> = {
    ...ASSET_CEILINGS,
    ...(options?.ceilings ?? {}),
  };
  const largestCount = Math.max(0, Math.floor(options?.largestCount ?? DEFAULT_LARGEST));

  const { files, skipped: inventorySkipped } = inventory(drop);
  const skipped = [...inventorySkipped];
  for (const sentence of Array.isArray(drop?.truncated) ? drop.truncated : []) {
    if (typeof sentence === "string" && sentence !== "") skipped.push(sentence);
  }

  const groups = groupByResource(drop, files);
  const labels = disambiguateLabels(
    groups.map((group) => ({
      name: group.name,
      identity: group.identity,
      origin: group.origin,
    })),
    EM_DASH_LABEL_STYLE,
  );
  const byBucket = emptyTallies();
  const byExtension = new Map<string, BudgetTally>();
  const allRows: BudgetFileRow[] = [];
  const resources: BudgetResourceRow[] = [];
  let totalBytes = 0;
  let totalFiles = 0;

  groups.forEach((group, index) => {
    const buckets = emptyTallies();
    const assets: BudgetFileRow[] = [];
    let bytes = 0;

    for (const file of group.files) {
      const bucket = bucketOf(file.path);
      const kind = assetKindOf(file.path);
      const ceiling = ceilings[kind] ?? ceilings.other;
      const row: BudgetFileRow = {
        path: file.path,
        name: baseName(file.path),
        extension: extensionOf(file.path),
        resource: labels[index],
        bucket,
        kind,
        bytes: file.bytes,
        tier: tierOf(file.bytes, ceiling.warn, ceiling.high, thresholds.nearFraction),
      };
      assets.push(row);
      allRows.push(row);
      add(buckets, bucket, file.bytes);
      add(byBucket, bucket, file.bytes);
      const extensionTally = byExtension.get(row.extension) ?? { bytes: 0, files: 0 };
      extensionTally.bytes += file.bytes;
      extensionTally.files += 1;
      byExtension.set(row.extension, extensionTally);
      bytes += file.bytes;
      totalBytes += file.bytes;
      totalFiles += 1;
    }

    assets.sort((a, b) => byBytesDesc(a, b) || compareText(a.path, b.path));
    resources.push({
      name: group.name,
      label: labels[index],
      identity: group.identity,
      origin: group.origin,
      kind: group.kind,
      bytes,
      files: group.files.length,
      buckets,
      assets,
    });
  });

  resources.sort((a, b) => byBytesDesc(a, b) || compareText(a.label, b.label));

  const largest = [...allRows]
    .sort((a, b) => byBytesDesc(a, b) || compareText(a.path, b.path))
    .slice(0, largestCount);

  const findings: GtaFinding[] = [];
  findings.push(...oversizeFindings(resources, ceilings, skipped));
  findings.push(...resourceFindings(resources, thresholds));
  findings.push(
    ...extensionFindings(
      [...byExtension.entries()].map(([extension, tally]) => ({ extension, ...tally })),
      totalBytes,
      thresholds,
    ),
  );
  findings.push(...textureShareFindings(resources, thresholds));

  const { pairs, findings: lodFindings } = identicalLodPairs(groups, labels, options, skipped);
  findings.push(...lodFindings);

  // Keyed by the *pair*, never by the path. Keying by path and suppressing any
  // group all of whose members appear in some pair is the defect this line
  // exists to not have: six vehicle packs each shipping `ws_wheel.ytd` and its
  // routine `+hi` twin is twelve copies of one dictionary and 132 MB of real
  // waste, and every one of the twelve is in a pair — so a path-keyed rule
  // deletes the highest-value finding this module has.
  const reportedLodPairs = new Set<string>();
  for (const pair of pairs) reportedLodPairs.add(lodPairKey(pair.basePath, pair.companionPath));

  const { duplicates, findings: duplicateFindings } = duplicateGroups(
    allRows,
    reportedLodPairs,
    thresholds,
    options,
    skipped,
  );
  findings.push(...duplicateFindings);

  return {
    totalBytes,
    totalFiles,
    byBucket,
    byResource: resources,
    byExtension: [...byExtension.entries()]
      .map(([extension, tally]) => ({ extension, ...tally }))
      .sort((a, b) => byBytesDesc(a, b) || compareText(a.extension, b.extension)),
    largest,
    duplicates,
    identicalLodPairs: pairs,
    // Ranked by the layer's own ordering rather than by the order the rules
    // happen to run in, so this list and PackBench's and the desktop report's
    // are the same list. `report.ts` makes the same argument for the same
    // reason.
    findings: sortFindings(dedupeFindings(findings)),
    thresholds,
    skipped: [...new Set(skipped)],
  };
}

// ─── Rule 1: one file over its own ceiling ───────────────────────────────────

function oversizeFindings(
  resources: readonly BudgetResourceRow[],
  ceilings: Record<BudgetAssetKind, BudgetCeiling>,
  skipped: string[],
): GtaFinding[] {
  const flagged: { row: BudgetFileRow; resource: BudgetResourceRow; tier: BudgetTier }[] = [];
  for (const resource of resources) {
    for (const row of resource.assets) {
      if (row.tier !== null) flagged.push({ row, resource, tier: row.tier });
    }
  }
  flagged.sort((a, b) => b.row.bytes - a.row.bytes || compareText(a.row.path, b.row.path));

  const findings: GtaFinding[] = [];
  for (const { row, resource, tier } of flagged.slice(0, MAX_FINDINGS_PER_RULE)) {
    const ceiling = ceilings[row.kind] ?? ceilings.other;
    const limit = tier === "high" ? ceiling.high : ceiling.warn;
    const headline =
      tier === "near"
        ? `${row.name} is approaching the ${ceiling.label} ceiling`
        : `${row.name} is a ${formatBudgetBytes(row.bytes)} ${ceiling.label}`;
    const measurement =
      tier === "near"
        ? `It is ${formatBudgetBytes(row.bytes)}, against a ceiling of ` +
          `${formatBudgetBytes(ceiling.warn)}.`
        : `That is at or over the ${formatBudgetBytes(limit)} mark for a ${ceiling.label}.`;
    findings.push({
      code: "budget.file.oversize",
      severity: TIER_SEVERITY[tier],
      title: headline,
      detail:
        `${measurement} Every client downloads it on connect and holds it in streaming ` +
        `memory while it is loaded. ${ceiling.advice}`,
      file: row.path,
      entity: { kind: "resource", id: resource.label },
    });
  }

  if (flagged.length > MAX_FINDINGS_PER_RULE) {
    const rest = flagged.length - MAX_FINDINGS_PER_RULE;
    skipped.push(
      `${rest.toLocaleString("en-US")} further file${rest === 1 ? "" : "s"} at or over a ceiling ` +
        `${rest === 1 ? "is" : "are"} counted in the tables but not listed individually, because ` +
        `a findings list past ${MAX_FINDINGS_PER_RULE} rows of the same sentence is not read.`,
    );
  }
  return findings;
}

// ─── Rules 2 and 3: one resource's total and file count ──────────────────────

function resourceFindings(
  resources: readonly BudgetResourceRow[],
  thresholds: BudgetThresholds,
): GtaFinding[] {
  const findings: GtaFinding[] = [];
  for (const resource of resources) {
    // The loose row is not a resource, and "this resource is over its ceiling"
    // about a bag of files that belong to no resource is a sentence nobody can
    // act on. Its bytes are still in the total and still on its own row.
    if (resource.kind !== "resource") continue;

    const sizeTier = tierOf(
      resource.bytes,
      thresholds.resourceBytesWarn,
      thresholds.resourceBytesHigh,
      thresholds.nearFraction,
    );
    if (sizeTier !== null) {
      const biggest = resource.assets[0];
      const bucket = BUDGET_BUCKETS.map((name) => ({ name, ...resource.buckets[name] })).sort(
        byBytesDesc,
      )[0];
      findings.push({
        code: "budget.resource.total",
        severity: TIER_SEVERITY[sizeTier],
        title:
          sizeTier === "near"
            ? `${resource.label} is approaching the per-resource ceiling`
            : `${resource.label} is ${formatBudgetBytes(resource.bytes)} across ` +
              `${resource.files.toLocaleString("en-US")} files`,
        detail:
          `The ceiling for one resource is ${formatBudgetBytes(thresholds.resourceBytesWarn)}, ` +
          `and this one is ${formatBudgetBytes(resource.bytes)}. Most of it is ` +
          `${BUDGET_BUCKET_LABELS[bucket.name].toLowerCase()} at ` +
          `${formatBudgetBytes(bucket.bytes)}` +
          (biggest ? `, the largest single file being ${biggest.name}` : "") +
          ". No single file here need be over its own ceiling for the resource to be the " +
          "largest thing a joining player waits for — split it, or drop what it does not use.",
        entity: { kind: "resource", id: resource.label },
      });
    }

    const countTier = tierOf(
      resource.files,
      thresholds.resourceFilesWarn,
      thresholds.resourceFilesHigh,
      thresholds.nearFraction,
    );
    if (countTier !== null) {
      findings.push({
        code: "budget.resource.count",
        severity: TIER_SEVERITY[countTier],
        title: `${resource.label} ships ${resource.files.toLocaleString("en-US")} files`,
        detail:
          `The ceiling is ${thresholds.resourceFilesWarn.toLocaleString("en-US")} files in one ` +
          "resource. File count costs separately from size: each one is a manifest entry to " +
          "resolve and a transfer to set up at join time, and a resource this wide is slow to " +
          "start even when its bytes are modest. Split it into several resources, or remove the " +
          "files nothing references.",
        entity: { kind: "resource", id: resource.label },
      });
    }
  }
  return findings;
}

// ─── Rule 4: one extension dominating the drop ───────────────────────────────

function extensionFindings(
  rows: readonly { extension: string; bytes: number; files: number }[],
  totalBytes: number,
  thresholds: BudgetThresholds,
): GtaFinding[] {
  if (totalBytes <= 0) return [];
  const findings: GtaFinding[] = [];
  for (const row of rows) {
    if (row.bytes < thresholds.extensionBytesFloor) continue;
    const share = row.bytes / totalBytes;
    // No near tier: a share has no ceiling anybody can act on, so "approaching
    // 70% .ytd" is a sentence with no remedy at the end of it.
    const tier = tierOf(share, thresholds.extensionShareWarn, thresholds.extensionShareHigh, 0);
    if (tier === null) continue;
    const label = row.extension === "" ? "files with no extension" : `.${row.extension} files`;
    findings.push({
      code: "budget.extension.total",
      severity: TIER_SEVERITY[tier],
      title: `${label} are ${formatShare(share)} of this drop`,
      detail:
        `${row.files.toLocaleString("en-US")} ${label} come to ` +
        `${formatBudgetBytes(row.bytes)} of ${formatBudgetBytes(totalBytes)}. One extension this ` +
        "far ahead of the rest usually means one decision made once and repeated: uncompressed " +
        "dictionaries, high-detail models with no LODs, or audio at a rate nothing plays. " +
        "Fixing it once fixes it everywhere, which makes this the cheapest megabyte in the drop " +
        "to get back.",
    });
  }
  return findings;
}

// ─── Rule 5: textures as a share of one resource ─────────────────────────────

function textureShareFindings(
  resources: readonly BudgetResourceRow[],
  thresholds: BudgetThresholds,
): GtaFinding[] {
  const findings: GtaFinding[] = [];
  for (const resource of resources) {
    if (resource.kind !== "resource") continue;
    if (resource.bytes < thresholds.textureBytesFloor) continue;
    const textureBytes = resource.assets
      .filter((row) => row.kind === "texture")
      .reduce((total, row) => total + row.bytes, 0);
    const share = textureBytes / resource.bytes;
    const tier = tierOf(share, thresholds.textureShareWarn, thresholds.textureShareHigh, 0);
    if (tier === null) continue;
    findings.push({
      code: "budget.texture.share",
      severity: TIER_SEVERITY[tier],
      title: `Textures are ${formatShare(share)} of ${resource.label}`,
      detail:
        `${formatBudgetBytes(textureBytes)} of this resource's ` +
        `${formatBudgetBytes(resource.bytes)} is texture dictionaries. If this is a texture or ` +
        "livery pack that is exactly what it should look like and there is nothing to do. If it " +
        "is a vehicle or a map, the dictionaries are almost certainly uncompressed: re-saving " +
        "them with DXT/BC compression typically returns two thirds of that figure with no " +
        "visible change in game.",
      entity: { kind: "resource", id: resource.label },
    });
  }
  return findings;
}

// ─── Rule 6: the same bytes shipped more than once ───────────────────────────

function signatureNote(kind: BudgetSignatureKind): string {
  return kind === "full-content"
    ? "The caller compared these files in full, so they are the same bytes."
    : `They match on size and on a hash of their first ${SIGNATURE_PREFIX_BYTES.toLocaleString("en-US")} ` +
        "bytes, which is strong evidence and not a byte-for-byte comparison — check before deleting.";
}

/**
 * The two paths of one high-detail pair, as one key.
 *
 * Sorted so the key does not depend on which of the two the pairing function
 * called the base, and `pathKey`-folded for the reason every other comparison
 * in this layer is.
 */
function lodPairKey(a: string, b: string): string {
  const left = pathKey(a);
  const right = pathKey(b);
  // `JSON.stringify` rather than a joining character, for the reason the
  // signature key below picks NUL: a path is whatever a stranger called their
  // folder, and a separator that can occur inside either half is not a
  // separator. JSON has no such character to pick.
  return JSON.stringify(left < right ? [left, right] : [right, left]);
}

/**
 * The files a comparison would ever have been asked about.
 *
 * Exactly what `duplicateCandidatePaths` returns, computed over the same rows:
 * over the floor *and* sharing a size with another file. The two sets have to
 * be the same set, because every sentence below counts against this one — and a
 * caveat that counts files which cannot be duplicates fires on every clean scan
 * and is then read exactly as carefully as the competitor's unqualified "Total
 * size". A file whose length occurs once in the drop is not an uncompared
 * duplicate; it is not a duplicate.
 */
function comparableRows(
  rows: readonly BudgetFileRow[],
  thresholds: BudgetThresholds,
): BudgetFileRow[] {
  const overFloor = rows.filter((row) => row.bytes >= thresholds.duplicateMinBytes);
  const counts = new Map<number, number>();
  for (const row of overFloor) counts.set(row.bytes, (counts.get(row.bytes) ?? 0) + 1);
  return overFloor.filter((row) => (counts.get(row.bytes) ?? 0) > 1);
}

function duplicateGroups(
  rows: readonly BudgetFileRow[],
  reportedLodPairs: ReadonlySet<string>,
  thresholds: BudgetThresholds,
  options: BudgetOptions | undefined,
  skipped: string[],
): { duplicates: BudgetDuplicateGroup[]; findings: GtaFinding[] } {
  const signatures = options?.signatures;
  const candidates = comparableRows(rows, thresholds);

  if (!signatures || signatures.size === 0) {
    if (candidates.length > 0) {
      skipped.push(
        `Duplicate detection did not run: no content signatures were supplied, so none of the ` +
          `${candidates.length.toLocaleString("en-US")} files over ` +
          `${formatBudgetBytes(thresholds.duplicateMinBytes)} that share a size with another ` +
          "file were compared. Those are the only files a comparison would have read, because " +
          "two files of different lengths cannot be the same file. That is not the same as " +
          "finding no duplicates.",
      );
    }
    return { duplicates: [], findings: [] };
  }

  const signed = signedByKey(signatures);

  // Keyed on the size as well as the signature, so a caller whose signature
  // scheme leaves the size out cannot fold two different files into one group.
  const groups = new Map<string, BudgetFileRow[]>();
  let compared = 0;
  for (const row of candidates) {
    const signature = signed.get(pathKey(row.path));
    if (signature === undefined) continue;
    compared += 1;
    // Joined on NUL, written as an escape rather than as a raw byte in the
    // source: a signature is the caller's own string and may contain anything,
    // and a separator that can occur inside either half is not a separator.
    const key = `${row.bytes}\u0000${signature}`;
    const bucket = groups.get(key) ?? [];
    bucket.push(row);
    groups.set(key, bucket);
  }

  if (compared < candidates.length) {
    const rest = candidates.length - compared;
    skipped.push(
      `${rest.toLocaleString("en-US")} of the ${candidates.length.toLocaleString("en-US")} files ` +
        `over ${formatBudgetBytes(thresholds.duplicateMinBytes)} that share a size with another ` +
        "file had no signature and were not compared, so a duplicate among them would not be " +
        "reported here.",
    );
  }

  const duplicates: BudgetDuplicateGroup[] = [];
  for (const [key, bucket] of groups) {
    if (bucket.length < 2) continue;
    // A `_hi` companion identical to its base is already reported, with the
    // remedy that actually applies to it, so *that pair* is not repeated here.
    // Only that pair, and only when the group is nothing else: a group with a
    // third copy, or with copies in two resources, is a duplication the
    // high-detail rule never looked at. Six vehicle packs each shipping one
    // dictionary and its routine `+hi` twin is twelve copies of one file — the
    // brief's headline example — and every one of them is in a pair, so a
    // predicate that asked "is every member in some pair" would suppress it.
    if (
      bucket.length === 2 &&
      reportedLodPairs.has(lodPairKey(bucket[0].path, bucket[1].path))
    ) {
      continue;
    }
    const bytes = bucket[0].bytes;
    duplicates.push({
      signature: key.split("\u0000")[1] ?? key,
      bytes,
      copies: bucket
        .map((row) => ({ path: row.path, resource: row.resource }))
        .sort((a, b) => compareText(a.path, b.path)),
      wastedBytes: bytes * (bucket.length - 1),
    });
  }
  duplicates.sort((a, b) => b.wastedBytes - a.wastedBytes || compareText(a.signature, b.signature));

  // The same rule `oversizeFindings` keeps, for the same reason and out loud:
  // a busy server folder with sixty duplicated groups would otherwise drop
  // twenty of them — real, recoverable megabytes — with nothing on the page
  // admitting it, because the surface renders findings and not `duplicates`.
  if (duplicates.length > MAX_FINDINGS_PER_RULE) {
    const rest = duplicates.slice(MAX_FINDINGS_PER_RULE);
    const bytes = rest.reduce((total, group) => total + group.wastedBytes, 0);
    skipped.push(
      `${rest.length.toLocaleString("en-US")} further ` +
        `${rest.length === 1 ? "file is" : "files are"} shipped more than once, ` +
        `${formatBudgetBytes(bytes)} of waste in total, counted in this report but not listed ` +
        `individually, because a findings list past ${MAX_FINDINGS_PER_RULE} rows of the same ` +
        "sentence is not read.",
    );
  }

  const note = signatureNote(options?.signatureKind ?? "size-and-prefix");
  const findings: GtaFinding[] = duplicates.slice(0, MAX_FINDINGS_PER_RULE).map((group) => {
    const shown = group.copies.slice(0, MAX_COPIES_NAMED).map((copy) => copy.path);
    const rest = group.copies.length - shown.length;
    const resources = [...new Set(group.copies.map((copy) => copy.resource))];
    return {
      code: "budget.duplicate",
      severity:
        group.wastedBytes >= thresholds.duplicateWasteWarn
          ? ("warning" as GtaSeverity)
          : ("info" as GtaSeverity),
      title:
        `${baseName(group.copies[0].path)} ships ${group.copies.length} times ` +
        `(${formatBudgetBytes(group.wastedBytes)} wasted)`,
      detail:
        `The same ${formatBudgetBytes(group.bytes)} file appears at ${shown.join(", ")}` +
        (rest > 0 ? ` and ${rest.toLocaleString("en-US")} more` : "") +
        `, across ${resources.length === 1 ? "one resource" : `${resources.length} resources`}. ` +
        `Every copy is downloaded by every client. ${note} Keep one and have the others depend ` +
        "on the resource that ships it.",
      file: group.copies[0].path,
    };
  });

  return { duplicates, findings };
}

// ─── Rule 7: a high-detail companion identical to its base ───────────────────

/** Intake gives paths relative to the drop; `GtaFile` is what the collector wants. */
function asGtaFile(file: SizedPath): GtaFile {
  return {
    path: file.path,
    name: baseName(file.path),
    extension: extensionOf(file.path),
    size: file.bytes,
  };
}

function assetNamed(assets: readonly StreamAsset[], basename: string): StreamAsset | undefined {
  return assets.find((asset) => asset.basename === basename);
}

/**
 * `police_hi.yft` byte-for-byte equal to `police.yft` doubles the download.
 *
 * The pairing is `highLodGroups`, which already knows that models use `_hi` and
 * texture dictionaries use `+hi` — a tool that normalises one and not the other
 * reports half of every correct vehicle as broken. What is added here is the
 * comparison: same resource, same size, same signature.
 *
 * A matching size alone is not reported as identical, and not reported as clean
 * either. It goes in `skipped` by count, because "these two 10 MB files are the
 * same length and nobody looked inside them" is a fact the user can act on and a
 * silence they cannot.
 */
function identicalLodPairs(
  groups: readonly ResourceGroup[],
  labels: readonly string[],
  options: BudgetOptions | undefined,
  skipped: string[],
): { pairs: BudgetLodPair[]; findings: GtaFinding[] } {
  const signed = signedByKey(options?.signatures);

  const pairs: BudgetLodPair[] = [];
  let sameSizeUncompared = 0;

  groups.forEach((group, index) => {
    if (group.kind !== "resource") return;
    const assets = collectStreamAssets([
      {
        name: group.name,
        identity: group.identity === "" ? group.name : group.identity,
        origin: group.origin,
        files: group.files.map(asGtaFile),
      },
    ]);

    for (const { base, companions } of highLodGroups(assets)) {
      const baseAsset = assetNamed(assets, base);
      if (!baseAsset) continue;
      for (const companion of companions) {
        const companionAsset = assetNamed(assets, companion);
        if (!companionAsset) continue;
        for (const basePlace of baseAsset.occurrences) {
          for (const companionPlace of companionAsset.occurrences) {
            if (basePlace.size !== companionPlace.size) continue;
            if (basePlace.size <= 0) continue;
            const baseSignature = signed.get(pathKey(basePlace.path));
            const companionSignature = signed.get(pathKey(companionPlace.path));
            if (baseSignature === undefined || companionSignature === undefined) {
              sameSizeUncompared += 1;
              continue;
            }
            if (baseSignature !== companionSignature) continue;
            pairs.push({
              basePath: basePlace.path,
              companionPath: companionPlace.path,
              resource: labels[index],
              bytes: basePlace.size,
            });
          }
        }
      }
    }
  });

  pairs.sort((a, b) => b.bytes - a.bytes || compareText(a.companionPath, b.companionPath));

  if (sameSizeUncompared > 0) {
    skipped.push(
      `${sameSizeUncompared.toLocaleString("en-US")} high-detail ` +
        `pair${sameSizeUncompared === 1 ? " was" : "s were"} the same size as the base asset and ` +
        "could not be compared, because no content signature was supplied for them. They may or " +
        "may not be the same file.",
    );
  }

  if (pairs.length > MAX_FINDINGS_PER_RULE) {
    const rest = pairs.slice(MAX_FINDINGS_PER_RULE);
    const bytes = rest.reduce((total, pair) => total + pair.bytes, 0);
    skipped.push(
      `${rest.length.toLocaleString("en-US")} further high-detail ` +
        `${rest.length === 1 ? "companion is" : "companions are"} identical to the base ` +
        `asset, ${formatBudgetBytes(bytes)} in total, counted in this report but not listed ` +
        `individually, because a findings list past ${MAX_FINDINGS_PER_RULE} rows of the same ` +
        "sentence is not read.",
    );
  }

  const note = signatureNote(options?.signatureKind ?? "size-and-prefix");
  const findings: GtaFinding[] = pairs.slice(0, MAX_FINDINGS_PER_RULE).map((pair) => ({
    code: "budget.lod.identical",
    severity: "warning" as GtaSeverity,
    title: `${baseName(pair.companionPath)} is identical to ${baseName(pair.basePath)}`,
    detail:
      `Both are ${formatBudgetBytes(pair.bytes)}, so this pack downloads the same asset twice ` +
      `and gains nothing for it: the high-detail copy exists to hold more geometry or a larger ` +
      `texture than the base, and this one holds exactly the same. ${note} Either export a real ` +
      "high-detail version or delete the companion — the game falls back to the base asset when " +
      "there is none.",
    file: pair.companionPath,
    entity: { kind: "resource", id: pair.resource },
  }));

  return { pairs, findings };
}

// ─── Export ──────────────────────────────────────────────────────────────────

const CRLF = "\r\n";

/**
 * The resource and file tables as one spreadsheet.
 *
 * Exact byte counts in their own columns rather than the formatted string: a
 * spreadsheet is the one place a reader will sort and sum, and a column of
 * "16.00 MiB" cannot do either. The formatted figure rides along beside it for
 * reading. `csvCell` is the layer's hardened cell — a resource name comes out of
 * a stranger's folder and a path is whatever they called it.
 *
 * The skipped list rides in the same table under its own `scope` rather than as
 * a banner above it, because a banner is what stops a CSV from parsing —
 * `report.ts` makes the same argument for the same shape. It is not optional
 * decoration: this is the export a server owner forwards to whoever packaged
 * the resource, and a spreadsheet of totals over a pack half of which was never
 * opened is the competitor's unqualified "Total size" with our name on it.
 * Filtering `scope` to `resource` and `file` gives a clean data table for
 * anyone who wants one.
 *
 * `note` is appended at the end rather than slotted in beside `path`, where it
 * would read better, because a column inserted mid-table renumbers every column
 * after it for anything reading this file positionally.
 *
 * JSON needs no function: a `BudgetReport` is plain data and `JSON.stringify`
 * is the export.
 */
export function budgetToCsv(report: BudgetReport): string {
  const lines: string[] = [];
  lines.push(
    ["scope", "resource", "path", "bucket", "kind", "bytes", "size", "tier", "note"].join(","),
  );
  for (const resource of report.byResource) {
    lines.push(
      [
        csvCell("resource"),
        csvCell(resource.label),
        "",
        "",
        csvCell(resource.kind),
        String(resource.bytes),
        csvCell(formatBudgetBytes(resource.bytes)),
        "",
        "",
      ].join(","),
    );
  }
  for (const resource of report.byResource) {
    for (const row of resource.assets) {
      lines.push(
        [
          csvCell("file"),
          csvCell(resource.label),
          csvCell(row.path),
          csvCell(row.bucket),
          csvCell(row.kind),
          String(row.bytes),
          csvCell(formatBudgetBytes(row.bytes)),
          csvCell(row.tier ?? ""),
          "",
        ].join(","),
      );
    }
  }
  for (const sentence of report.skipped) {
    lines.push(
      [csvCell("skipped"), "", "", "", "", "", "", "", csvCell(sentence)].join(","),
    );
  }
  return lines.join(CRLF) + CRLF;
}

/**
 * The report as a paste.
 *
 * Ends with what was not counted, because a total handed to somebody else
 * without its caveats is the competitor's "Total size" with our name on it.
 */
export function budgetToMarkdown(report: BudgetReport): string {
  const out: string[] = [];
  out.push("# Stream budget");
  out.push("");
  out.push(
    `${formatBudgetBytes(report.totalBytes)} across ` +
      `${report.totalFiles.toLocaleString("en-US")} files and ` +
      `${report.byResource.length.toLocaleString("en-US")} rows. On-disk size, not VRAM.`,
  );
  out.push("");
  out.push("| Bucket | Size | Files |");
  out.push("|---|---|---|");
  for (const bucket of BUDGET_BUCKETS) {
    const tally = report.byBucket[bucket];
    out.push(
      `| ${BUDGET_BUCKET_LABELS[bucket]} | ${formatBudgetBytes(tally.bytes)} | ` +
        `${tally.files.toLocaleString("en-US")} |`,
    );
  }
  out.push("");
  out.push("| Resource | Size | Files |");
  out.push("|---|---|---|");
  for (const resource of report.byResource) {
    out.push(
      `| ${resource.label} | ${formatBudgetBytes(resource.bytes)} | ` +
        `${resource.files.toLocaleString("en-US")} |`,
    );
  }
  if (report.findings.length > 0) {
    out.push("");
    out.push("## Findings");
    out.push("");
    for (const finding of report.findings) {
      out.push(`- **${finding.title}** — ${finding.detail}`);
    }
  }
  if (report.skipped.length > 0) {
    out.push("");
    out.push("## Not counted, not compared, not listed");
    out.push("");
    for (const sentence of report.skipped) out.push(`- ${sentence}`);
  }
  return out.join("\n") + "\n";
}
